/**
 * API 服务层
 * 统一封装后端所有接口调用，管理 token 注入和错误处理
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

// ============ 工具函数 ============

function getToken(): string | null {
  return localStorage.getItem('token')
}

/** token 过期处理：清空登录态并跳转到对应登录页 */
function handleTokenExpired(): void {
  // 清除所有登录相关缓存
  localStorage.removeItem('token')
  localStorage.removeItem('auth_user')
  localStorage.removeItem('userAccount')
  localStorage.removeItem('adminLoggedIn')

  // 根据当前路径跳转到对应登录页（replace 防止后退回到过期页面）
  const path = window.location.pathname
  let loginUrl = '/user/login'
  if (path.startsWith('/admin')) {
    loginUrl = '/admin/login'
  } else if (path.startsWith('/medical')) {
    loginUrl = '/medical/login'
  }
  window.location.replace(loginUrl)
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  params?: Record<string, unknown>,
  withAuth = true
): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (withAuth && token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  let url = `${BASE_URL}${path}`
  if (params) {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&')
    if (qs) url += `?${qs}`
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    // token 过期或无效 → 跳转到登录页，不再执行后续逻辑
    if (res.status === 401) {
      if (!withAuth) {
        // 登录/注册等无需 auth 的接口：401 表示密码/凭证错误，不触发 token 过期处理
        const err = await res.json().catch(() => ({ detail: '登录失败，请检查账号密码' }))
        throw new Error(err.detail || '登录失败，请检查账号密码')
      }
      handleTokenExpired()
      throw new Error('token_expired')
    }

    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
    const detail = Array.isArray(err.detail)
      ? err.detail.map((e: { msg?: string }) => e.msg || JSON.stringify(e)).join('；')
      : (err.detail || `请求失败 (${res.status})`)
    const error = new Error(detail) as Error & { statusCode?: number }
    error.statusCode = res.status
    throw error
  }

  // 204 No Content
  if (res.status === 204) return undefined as T
  return res.json()
}

const get = <T>(path: string, params?: Record<string, unknown>, withAuth = true) =>
  request<T>('GET', path, undefined, params, withAuth)
const post = <T>(path: string, body?: unknown, params?: Record<string, unknown>, withAuth = true) =>
  request<T>('POST', path, body, params, withAuth)
const put = <T>(path: string, body?: unknown, params?: Record<string, unknown>, withAuth = true) =>
  request<T>('PUT', path, body, params, withAuth)
const patch = <T>(path: string, body?: unknown) =>
  request<T>('PATCH', path, body)
const del = <T>(path: string) =>
  request<T>('DELETE', path)

// ============ 类型定义 ============

export interface AccountInfo {
  id: string
  phone: string
  name: string
  role: string
  medical_role?: string
  hospital_id?: string
  hospital_name?: string
  member_no?: string
  id_card?: string
  avatar?: string
  status: string
  created_at?: string
  last_login_at?: string
}

export interface LoginResponse {
  token: string
  account: AccountInfo
  expires_at: string
}

export interface UserInfoResponse {
  id: string
  account_id: string
  member_no?: string
  points: number
  phone?: string
  name?: string
  id_card?: string
  avatar?: string
  status: string
  last_active_at?: string
  created_at?: string
  commitment_signed_at?: string
  hospital_id?: string
  crowd_type?: string
  age_group?: string
  tags?: string
  exchange_count: number  // 累计兑换次数
  event_count: number     // 累计参与活动次数
}

export interface PageParams {
  page?: number
  page_size?: number
  keyword?: string
}

export interface DelegatedUser {
  user_id: string
  account_id: string
  name: string
  phone: string
  id_card_masked: string
  points: number
  hospital_id?: string | null
  hospital_name?: string | null
  is_bound: boolean
  status: string
}

export interface DelegatedGift {
  id: string
  name: string
  image?: string | null
  points: number
  stock: number
  category?: string | null
  exchange_limit: number
  exchanged_count: number
  remaining_limit?: number | null
  delivery_type: 'express' | 'selfpickup' | 'both'
  can_exchange: boolean
  disabled_reason?: string | null
}

export interface DelegatedGiftContext {
  user: {
    user_id: string
    account_id: string
    name: string
    phone: string
    points: number
    hospital_id?: string | null
    is_bound: boolean
  }
  hospital: { id: string; name: string }
  gifts: DelegatedGift[]
  total: number
  page: number
  page_size: number
  total_pages: number
  categories: string[]
}

// ============ Auth ============

export const authApi = {
  /** 管理员/医护 账号密码登录 */
  login: (phone: string, password: string) =>
    post<LoginResponse>('/auth/login', { phone, password }, undefined, false),

  /** 微信登录 */
  wechatLogin: (code: string, phone?: string) =>
    post<LoginResponse>('/auth/wechat-login', { code, phone }, false),

  /** 用户注册 */
  register: (data: {
    phone: string
    password: string
    name: string
    id_card?: string
    code: string
    hospital_id: string
    crowd_type?: string
    age_group: string
  }) => post<LoginResponse>('/auth/register', data, false),

  /** 激活医护账号 */
  activate: (data: { phone: string; id_card: string; password: string; sms_code: string }) =>
    post('/auth/activate', data, false),

  /** 更换手机号 */
  changePhone: (new_phone: string, sms_code: string) =>
    post('/auth/change-phone', { new_phone, sms_code }),

  /** 修改密码 */
  changePassword: (old_password: string, new_password: string) =>
    post('/auth/change-password', { old_password, new_password }),

  /** 发送短信验证码（可选 purpose） */
  sendCode: (phone: string, purpose?: string) =>
    post('/auth/send-code', { phone, purpose: purpose || 'register' }, false),

  /** 验证短信验证码 */
  verifyCode: (phone: string, code: string, purpose: string) =>
    post('/auth/verify-code', { phone, code, purpose }, false),

  /** 通过短信验证码修改密码 */
  changePasswordBySms: (phone: string, code: string, new_password: string) =>
    post('/auth/change-password-by-sms', { phone, code, new_password }),

  /** 修改密码（验证码已验证，直接设置新密码） */
  changePasswordSimple: (new_password: string) =>
    post('/auth/change-password-simple', { new_password }),

  /** 获取当前账号信息（含统计数据） */
  me: () => get<UserInfoResponse>('/users/me'),

  /** 刷新用户信息并更新 localStorage，返回转换后的数据 */
  refreshUserInfo: async (): Promise<any | null> => {
    try {
      const resp = await get<UserInfoResponse>('/users/me')
      if (resp && resp.status !== 'guest') {
        // 转换为 camelCase 后存储（兼容前端期望的字段名）
        const transformed = {
          id: resp.id,
          accountId: resp.account_id,
          memberNo: resp.member_no,
          points: resp.points,
          phone: resp.phone,
          name: resp.name,
          idCard: resp.id_card,
          avatar: resp.avatar,
          status: resp.status,
          lastActiveAt: resp.last_active_at,
          createdAt: resp.created_at,
          commitmentSignedAt: resp.commitment_signed_at,
          hospitalId: resp.hospital_id,
          crowdType: resp.crowd_type,
          ageGroup: resp.age_group,
          tags: resp.tags,
          exchangeCount: resp.exchange_count,
          eventCount: resp.event_count,
          unreadCount: resp.unread_count,
          // 角色信息（医护判断）
          role: resp.role,
          medicalRole: resp.medical_role,
        }
        localStorage.setItem('userAccount', JSON.stringify(transformed))
        // 用缓存补充 hospitalName
        try {
          const raw = localStorage.getItem(HOSPITAL_CACHE_KEY)
          if (raw && transformed.hospitalId) {
            const cache: Record<string, CachedHospital> = JSON.parse(raw)
            const item = cache[transformed.hospitalId]
            if (item && Date.now() - item.cachedAt < 7 * 24 * 60 * 60 * 1000) {
              transformed.hospitalName = item.name
              localStorage.setItem('userAccount', JSON.stringify(transformed))
            }
          }
        } catch {
          // 本地缓存损坏时忽略，继续使用接口返回的数据
        }
        return transformed
      }
      return resp
    } catch {
      return null
    }
  },

  /** 更新健康信息（补全资料） */
  updateProfile: (data: {
    hospital_id?: string
    crowd_type?: string
    age_group?: string
  }) => put('/users/me', data),
}

// ============ 用户管理 ============

export const usersApi = {
  list: (params?: PageParams & { status?: string; hospital_id?: string }) =>
    get('/users', params),
  detail: (id: string) => get(`/users/${id}`),
  create: (data: { phone: string; password: string; name: string; id_card?: string }) =>
    post('/users/admin-create', data),
  update: (id: string, data: { name?: string; phone?: string; id_card?: string; password?: string; status?: string; hospital_id?: string; crowd_type?: string; age_group?: string; tags?: string }) =>
    put(`/users/${id}`, data),
  // 更新自己的信息
  updateMe: (data: { name?: string; avatar?: string }) =>
    put('/users/me', data),
  freeze: (accountId: string) => put(`/users/${accountId}/freeze`),
  unfreeze: (accountId: string) => put(`/users/${accountId}/unfreeze`),
  adjustPoints: (userId: string, points: number, reason: string) =>
    put(`/users/${userId}/points`, { points, reason }),
}

// ============ 医护管理 ============

export const medicalApi = {
  list: (params?: PageParams & { hospital_id?: string; role?: string }) =>
    get('/medical/', params),
  detail: (id: string) => get(`/medical/${id}`),
  // 获取当前医护账号信息
  me: () => get('/medical/me'),
  create: (data: {
    phone: string
    name: string
    id_card?: string
    hospital_id: string
    medical_role: string
  }) => post('/medical/', data),
  update: (id: string, data: Record<string, unknown>) => put(`/medical/${id}`, data),
  // 医护端搜索用户（用于积分录入）
  searchUsers: (keyword: string, limit?: number) =>
    get('/medical/search-users', { keyword, limit }),
  // 根据用户ID获取用户信息（用于扫码识别）
  getUserById: (userId: string) => get(`/medical/users/${userId}`),
  // 获取机构内的用户和医护列表
  getUsersInHospital: (params?: PageParams & { keyword?: string; status?: string }) =>
    get('/medical/users-in-hospital', params),
  updateStatus: (id: string, action: 'freeze' | 'unfreeze') =>
    patch(`/medical/${id}/status`, { action }),
  freeze: (id: string) => patch(`/medical/${id}/status`, { action: 'freeze' }),
  unfreeze: (id: string) => patch(`/medical/${id}/status`, { action: 'unfreeze' }),
  delete: (id: string) => del(`/medical/${id}`),
}

// ============ 机构管理 ============

export const hospitalsApi = {
  list: (params?: PageParams) => get('/hospitals', params, false),
  detail: (id: string) => get(`/hospitals/${id}`, undefined, false),
  create: (data: {
    name: string
    address?: string
    phone?: string
    manager?: string
    remark?: string
    status?: string
    sort_order?: number
  }) => post('/hospitals', data),
  update: (id: string, data: Record<string, unknown>) => put(`/hospitals/${id}`, data),
  delete: (id: string) => del(`/hospitals/${id}`),
}

// ============ 事件类型 ============

export const eventsApi = {
  list: (params?: PageParams & { is_active?: boolean }) => {
    // 转换 is_active → enabled（后端使用 enabled int 字段）
    const query: Record<string, unknown> = { ...params }
    if (params?.is_active !== undefined) {
      query.enabled = params.is_active ? 1 : 0
      delete query.is_active
    }
    return get('/events', query, true)
  },
  detail: (id: string) => get(`/events/${id}`, undefined, false),
  create: (data: { name: string; points: number; description?: string }) =>
    post('/events', data),
  update: (id: string, data: Record<string, unknown>) => put(`/events/${id}`, data),
  delete: (id: string) => del(`/events/${id}`),
  toggle: (id: string, is_active: boolean) =>
    put(`/events/${id}/toggle`, { is_active }),
}

// ============ 礼品管理 ============

export const giftsApi = {
  list: (params?: PageParams & { is_active?: boolean; hospital_id?: string }, withAuth = true) => {
    // 转换 is_active → enabled（后端使用 enabled int 字段）
    const query: Record<string, unknown> = { ...params }
    if (params?.is_active !== undefined) {
      query.enabled = params.is_active ? 1 : 0
      delete query.is_active
    }
    return get('/gifts', query, withAuth)
  },
  detail: (id: string, withAuth = true) => get(`/gifts/${id}`, undefined, withAuth),
  create: (data: {
    name: string
    points_required: number
    stock: number
    description?: string
    image_url?: string
    exchange_limit?: number
  }) => post('/gifts', data),
  update: (id: string, data: Record<string, unknown>) => put(`/gifts/${id}`, data),
  delete: (id: string) => del(`/gifts/${id}`),
  toggle: (id: string, is_active: boolean) =>
    put(`/gifts/${id}/toggle`, { is_active }),
}

// ============ 礼品分类 ============

export const giftCategoriesApi = {
  list: () => get<{ categories: { id: string; name: string; sort_order: number; created_at?: string }[] }>('/gift-categories'),
  create: (name: string, sort_order: number) =>
    post('/gift-categories', undefined, { name, sort_order }),
  update: (id: string, name: string, sort_order: number) =>
    put(`/gift-categories/${id}`, undefined, { name, sort_order }),
  reorder: (orders: { id: string; sort_order: number }[]) =>
    put('/gift-categories/reorder', orders),
  remove: (id: string) => del(`/gift-categories/${id}`),
}

// ============ 积分记录 ============

export const pointsApi = {
  /** 查询积分记录 */
  records: (params?: {
    user_id?: string
    status?: string
    event_type_id?: string
    start_date?: string
    end_date?: string
    page?: number
    page_size?: number
  }) => get('/points/records', params),

  /** 录入积分记录（医护） */
  create: (data: {
    user_id: string
    event_type_id: string
    hospital_id?: string
    remark?: string
    entry_time?: string
  }) => post('/points/record', data),

  /** 确认积分 */
  confirm: (recordId: string) => put(`/points/records/${recordId}/confirm`),

  /** 撤销积分 */
  cancel: (recordId: string, reason?: string) => put(`/points/records/${recordId}/cancel${reason ? `?cancel_reason=${encodeURIComponent(reason)}` : ''}`),

  /** 今日统计（医护视角） */
  todayStats: () => get('/points/today-stats'),
}

// ============ 兑换记录 ============

export const exchangesApi = {
  /** 轻量统计：各状态数量 */
  stats: (params?: { user_id?: string }) => get('/exchanges/stats', params),

  list: (params?: {
    status?: string
    page?: number
    page_size?: number
    user_id?: string
    delegated_only?: boolean
  }) => get('/exchanges', params),

  detail: (id: string) => get(`/exchanges/${id}`),

  /** 申请兑换（用户） */
  create: (params: {
    gift_id: string
    hospital_id: string
    points: number
    delivery_type: 'selfpickup' | 'express'
    receiver_name?: string
    address?: string
    phone?: string
  }) => post('/exchanges', params),

  /** 管理员确认兑换 */
  confirm: (id: string) => put(`/exchanges/${id}/confirm`),

  /** 取消兑换（用户） */
  cancel: (id: string, reason?: string) => put(`/exchanges/${id}/cancel`, { reason }),

  /** 标记完成（管理员） */
  complete: (id: string) => put(`/exchanges/${id}/complete`),

  /** 用户确认收货 */
  userConfirm: (id: string) => put(`/exchanges/${id}/user-confirm`),

  /** 更新快递信息 */
  updateShipping: (id: string, tracking_no: string, shipping_company: string) =>
    put(`/exchanges/${id}/shipping`, { tracking_no, shipping_company }),
}

export const delegatedExchangesApi = {
  searchUsers: (keyword: string, limit = 20) =>
    get<{ items: DelegatedUser[] }>('/delegated-exchanges/users', { keyword, limit }),

  giftContext: (
    userId: string,
    hospitalId?: string,
    params?: { keyword?: string; category?: string; page?: number; page_size?: number },
  ) =>
    get<DelegatedGiftContext>('/delegated-exchanges/gifts', {
      user_id: userId,
      hospital_id: hospitalId,
      ...params,
    }),

  create: (data: {
    user_id: string
    gift_id: string
    delivery_type: 'selfpickup' | 'express'
    hospital_id?: string
    receiver_name?: string
    address?: string
    phone?: string
  }) => post('/delegated-exchanges', data),

  cancel: (exchangeId: string, reason: string) =>
    put(`/delegated-exchanges/${exchangeId}/cancel`, { reason }),
}

// ============ 签到 ============

export const checkinApi = {
  /** 每日签到 */
  checkin: () => post('/users/checkin'),
  /** 获取签到状态 */
  status: () => get('/users/checkin/status'),
}

// ============ 统计 ============

export const statisticsApi = {
  usersOverview: (params?: { start_date?: string; end_date?: string }) => get('/statistics/users/overview', params),
  pointsOverview: (params?: { start_date?: string; end_date?: string }) => get('/statistics/points/overview', params),
  pointsDaily: (params?: { start_date?: string; end_date?: string }) => get('/statistics/points/daily', params),
  pointsByEvent: (params?: { start_date?: string; end_date?: string }) => get('/statistics/points/by-event', params),
  exchangesOverview: (params?: { start_date?: string; end_date?: string }) => get('/statistics/exchanges/overview', params),
  popularGifts: (params?: { start_date?: string; end_date?: string }) => get('/statistics/exchanges/popular-gifts', params),
  hospitalRanking: (params?: { start_date?: string; end_date?: string }) => get('/statistics/hospitals', params),
  crowdStats: (params?: { hospital_id?: string }) => get('/statistics/users/crowd-stats', params),
  crowdStatsByHospitals: () => get('/statistics/users/crowd-stats/by-hospitals'),
  matrixCurrentMonth: () => get('/statistics/matrix/current-month'),
  matrixCumulative: () => get('/statistics/matrix/cumulative'),
}

// ============ 风控 ============

export const riskApi = {
  list: (params?: PageParams & { status?: string }) =>
    get('/risk/alerts', params),
  detail: (id: string) => get(`/risk/alerts/${id}`),
  review: (id: string, action: 'normal' | 'deduct' | 'freeze', review_note?: string) =>
    put(`/risk/alerts/${id}/review`, { action, review_note }),
}

// ============ 审计日志 ============

export const auditApi = {
  list: (params?: {
    action?: string
    operator_id?: string
    start_date?: string
    end_date?: string
    page?: number
    page_size?: number
  }) => get('/audit/logs', params),
}

// ============ 消息通知 ============

export const messagesApi = {
  list: (params?: { is_read?: boolean; page?: number; page_size?: number }) =>
    get('/messages', params),
  markRead: (id: string) => put(`/messages/${id}/read`),
  markAllRead: () => put('/messages/read-all'),
  unreadCount: () => get<{ count: number }>('/messages/unread-count'),
}

// ============ 导出 ============

export const exportApi = {
  points: (params?: Record<string, unknown>) =>
    get('/export/points', params),
  gifts: () => get('/export/gifts'),
  statistics: () => get('/export/statistics'),
}

// ============ 批量操作 ============

export const batchApi = {
  importUsers: (csvText: string) =>
    post('/batch/import/users', { data: csvText }),
  importPoints: (csvText: string) =>
    post('/batch/import/points', { data: csvText }),
}

// ============ 管理员 ============

export const adminsApi = {
  me: () => get('/admins/me'),
  list: (params?: PageParams) => get('/admins/', params),
  create: (data: {
    phone: string
    name: string
    password: string
    admin_level?: string
    hospital_id?: string
  }) => post('/admins/', data),
  update: (id: string, data: Record<string, unknown>) => put(`/admins/${id}`, data),
  delete: (id: string) => del(`/admins/${id}`),
}

// ============ 系统设置 ============

export const settingsApi = {
  // 获取所有系统配置
  getSystem: () => get('/settings/system'),

  // 更新系统配置
  updateSystem: (settings: Record<string, unknown>) =>
    put('/settings/system', settings),

  // 获取承诺书配置
  getCommitment: () => get('/settings/commitment'),

  // 更新承诺书配置
  updateCommitment: (data: { content?: string; enabled?: boolean }) =>
    put('/settings/commitment', data),

  // 签署承诺书
  signCommitment: () => post('/settings/commitment/sign'),

  // 获取签署记录
  getSignatures: (params?: { page?: number; page_size?: number; user_id?: string }) =>
    get('/settings/commitment/signatures', params),

  // 获取每月答题配置（用户端调用，无需 token）
  getMonthlyQuiz: () => get('/settings/feature/monthly-quiz', undefined, false),

  // 更新每月答题配置（管理端调用）
  updateMonthlyQuiz: (data: { enabled: boolean; qrCodeUrl: string; appName: string }) =>
    put('/settings/feature/monthly-quiz', data),

  // 获取小调查配置（用户端调用，无需 token）
  getSurvey: () => get('/settings/feature/survey', undefined, false),

  // 更新小调查配置（管理端调用）
  updateSurvey: (data: { enabled: boolean; qrCodeUrl: string; appName: string }) =>
    put('/settings/feature/survey', data),

  // 每日阅读奖励配置（用户端可读规则，管理端可写）
  getContentReadReward: () => get<{ enabled: boolean; minutes: number; points: number }>('/settings/feature/content-read-reward', undefined, false),
  updateContentReadReward: (data: { enabled: boolean; minutes: number; points: number }) =>
    put<{ message: string; minutes: number; points: number }>('/settings/feature/content-read-reward', data),

  // 检查用户是否已签署
  checkSigned: (userId: string) => get(`/settings/commitment/check/${userId}`),

  // 获取用户协议和隐私协议
  getAgreements: () => get('/settings/agreements'),

  // 更新用户协议和隐私协议
  updateAgreements: (data: { userAgreement?: string; privacyPolicy?: string }) =>
    put('/settings/agreements', data),
}

// ============ MCP 文章创作管理（V2.3） ============

export interface McpKeyItem {
  id: string
  key_id: string
  name: string
  scopes: string
  enabled: boolean
  preview_enabled: boolean
  draft_create_enabled: boolean
  rate_limit_per_minute: number
  revoked: boolean
  revoked_at?: string | null
  expires_at?: string | null
  created_at?: string | null
  last_used_at?: string | null
  full_key?: string // 仅创建时返回一次
}

export const mcpAdminApi = {
  // Key 列表（含全局开关状态）
  listKeys: () => get<{ items: McpKeyItem[]; global_enabled: boolean }>('/admin/mcp/keys'),

  // 生成新 Key（full_key 明文仅此一次返回）
  createKey: (data: { name?: string; scopes?: string; rate_limit_per_minute?: number }) =>
    post<McpKeyItem>('/admin/mcp/keys', data),

  // 修改 Key（开关/限流/名称）
  updateKey: (
    id: string,
    data: {
      name?: string
      enabled?: boolean
      preview_enabled?: boolean
      draft_create_enabled?: boolean
      rate_limit_per_minute?: number
    }
  ) => patch<McpKeyItem>(`/admin/mcp/keys/${id}`, data),

  // 撤销 Key（不可逆）
  revokeKey: (id: string) => post<{ revoked: boolean; key_id: string }>(`/admin/mcp/keys/${id}/revoke`),

  // 全局紧急开关
  setGlobalSwitch: (enabled: boolean) =>
    put<{ enabled: boolean }>('/admin/mcp/global-switch', { enabled }),
}

// ============ 机构信息缓存 ============

const HOSPITAL_CACHE_KEY = 'hospitalCache'

interface CachedHospital {
  id: string
  name: string
  cachedAt: number
}

/** 从 localStorage 读取机构缓存（7天过期） */
export function getHospitalCache(): Record<string, string> {
  try {
    const raw = localStorage.getItem(HOSPITAL_CACHE_KEY)
    if (!raw) return {}
    const cache: Record<string, CachedHospital> = JSON.parse(raw)
    const now = Date.now()
    const result: Record<string, string> = {}
    for (const [id, item] of Object.entries(cache)) {
      if (now - item.cachedAt < 7 * 24 * 60 * 60 * 1000) {
        result[id] = item.name
      }
    }
    return result
  } catch {
    return {}
  }
}

/** 保存机构列表到缓存（增量合并） */
export function saveHospitalToCache(hospitals: { id: string; name: string }[]): void {
  try {
    const existing = getHospitalCache()
    for (const h of hospitals) {
      existing[h.id] = h.name
    }
    const cache: Record<string, CachedHospital> = {}
    const now = Date.now()
    for (const [id, name] of Object.entries(existing)) {
      cache[id] = { id, name, cachedAt: now }
    }
    localStorage.setItem(HOSPITAL_CACHE_KEY, JSON.stringify(cache))
  } catch { /* ignore */ }
}

/** 根据 hospitalId 获取机构名称，查不到返回 null */
export function getHospitalNameFromCache(hospitalId: string | undefined): string | null {
  if (!hospitalId) return null
  const cache = getHospitalCache()
  return cache[hospitalId] || null
}

/** 尝试用缓存更新 userAccount 中的 hospitalName */
export function updateUserAccountHospitalName(): void {
  try {
    const raw = localStorage.getItem('userAccount')
    if (!raw) return
    const account = JSON.parse(raw)
    if (account.hospitalId) {
      const cache = getHospitalCache()
      account.hospitalName = cache[account.hospitalId] || null
      localStorage.setItem('userAccount', JSON.stringify(account))
    }
  } catch { /* ignore */ }
}

// ============ 文件上传 ============

export const uploadApi = {
  /**
   * 上传图片文件，返回 URL
   * @param file 文件对象
   * @param onProgress 可选的进度回调
   */
  uploadImage: (file: File, onProgress?: (pct: number) => void): Promise<string> => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('token')
      const xhr = new XMLHttpRequest()
      const formData = new FormData()
      formData.append('file', file)

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const res = JSON.parse(xhr.responseText)
            resolve(res.url)
          } catch {
            reject(new Error('解析响应失败'))
          }
        } else {
          try {
            const err = JSON.parse(xhr.responseText)
            reject(new Error(err.detail || `上传失败 (${xhr.status})`))
          } catch {
            reject(new Error(`上传失败 (${xhr.status})`))
          }
        }
      })

      xhr.addEventListener('error', () => reject(new Error('网络错误')))
      xhr.addEventListener('abort', () => reject(new Error('上传已取消')))

      xhr.open('POST', `${BASE_URL}/upload`)
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.send(formData)
    })
  },

  /**
   * 一键导入公众号文章：粘贴 mp.weixin.qq.com 文章链接，后端抓取解析。
   * 返回标题 / 简介 / 正文 HTML（图片已转存本站 /static/uploads）。
   * 为什么要走后端：公众号无 CORS + 图片 Referer 防盗链，见后端 wechat_import.py。
   */
  importWechatArticle: (url: string) =>
    post<{ title: string; summary: string; body_html: string; image_count: number; failed_images: number }>(
      '/upload/import-wechat',
      { url },
    ),
}

// ============ 内容（V2） ============

export interface ContentCategory {
  id: string
  parent_id?: string | null
  code: string
  name: string
  level: number
  sort_order: number
  is_active: number
  icon_url?: string | null
  description?: string
  children?: ContentCategory[]
}

export interface ContentAudience {
  id: string
  code: string
  name: string
  sort_order: number
  is_active: number
}

export interface ContentAttachment {
  id: string
  content_id: string
  attachment_type: 'image' | 'video' | 'audio'
  file_name: string
  file_url: string
  mime_type: string
  file_size: number
  duration_seconds?: number | null
  width?: number | null
  height?: number | null
  sort_order: number
  is_primary: number
  status: string
}

export interface ContentListItem {
  id: string
  category_id: string
  category_name?: string | null
  parent_category_id?: string | null
  parent_category_name?: string | null
  audience_id?: string | null
  audience_name?: string | null
  media_type: 'text' | 'video' | 'audio'
  title: string
  summary?: string | null
  cover_url?: string | null
  cover_attachment_id?: string | null
  author?: string | null
  county_code?: string | null
  plan_month?: string | null
  /** 外链地址：非空时用户端点击该条目直接跳转，不进详情页（总后台专属字段） */
  link_url?: string | null
  status: 'draft' | 'pending_review' | 'approved' | 'published' | 'offline' | 'rejected'
  is_recommended: number
  view_count: number
  like_count?: number
  favorite_count?: number
  helpful_count?: number
  share_count?: number
  sort_order: number
  published_at?: string | null
  submitted_at?: string | null
  reviewed_by?: string | null
  reviewed_at?: string | null
  review_note?: string | null
  scheduled_at?: string | null
  auto_offline_at?: string | null
  created_at?: string | null
  // 作者归属（投稿权限开放）— 仅管理端/作者端返回，用户端为 null
  author_account_id?: string | null
  author_name?: string | null
  author_hospital_id?: string | null
  author_hospital_name?: string | null
  author_role?: 'admin' | 'supervisor' | 'staff' | 'mcp' | 'user' | null
  withdrawn_at?: string | null
}

/** 作者端身份与权限范围（GET /author/content/me） */
export interface AuthorContext {
  account_id: string
  name?: string | null
  role: 'admin' | 'supervisor' | 'staff'
  hospital_id?: string | null
  hospital_name?: string | null
  can_audit: boolean
  can_publish: boolean
  view_scope: 'all' | 'hospital' | 'self'
  write_scope: 'all' | 'self'
}

export interface AuthorStats {
  total: number
  draft: number
  pending_review: number
  published: number
  rejected: number
  offline: number
}

export interface ContentTag {
  id: string
  name: string
  slug: string
  use_count: number
  is_active: number
}

export interface ContentComment {
  id: string
  content_id: string
  user_id: string
  user_name?: string | null
  user_phone?: string | null
  parent_id?: string | null
  body: string
  status: 'visible' | 'hidden' | 'pending'
  like_count: number
  reply_count: number
  created_at: string
  replies?: ContentComment[]
}

export interface AdminContentComment extends ContentComment {
  content_title?: string | null
  ip_address?: string | null
  updated_at?: string | null
  hidden_by?: string | null
  hidden_at?: string | null
}

export type SensitiveAction = 'block' | 'replace' | 'review'

export interface SensitiveWordItem {
  id: string
  word: string
  category: SensitiveAction
  replacement?: string | null
  is_active: number
  created_at?: string
  updated_at?: string
}

export interface SensitiveWordListResponse {
  total: number
  items: SensitiveWordItem[]
}

export interface ContentDetail extends ContentListItem {
  body?: string | null
  source?: string | null
  attachments: ContentAttachment[]
  related: ContentListItem[]
}

export interface ContentListResponse {
  total: number
  page: number
  page_size: number
  items: ContentListItem[]
}

export interface ContentHotItem {
  id: string
  title: string
  media_type: string
  category_name?: string | null
  view_count: number
}

export interface ContentStatsOverview {
  total: number
  draft: number
  published: number
  offline: number
  pending_review: number
  by_category: Record<string, number>
  by_media_type: Record<string, number>
  recent_views: number
  today_new: number
  days: number
  top_contents: ContentHotItem[]
}

export const contentApi = {
  // 用户端
  getCategoryTree: () => get<ContentCategory[]>('/content/categories/tree'),
  getAudiences: () => get<ContentAudience[]>('/content/audiences'),
  listPublic: (params?: Record<string, unknown>) => get<ContentListResponse>('/content/list', params, false),
  getDetail: (id: string) => get<ContentDetail>(`/content/${id}`, undefined, false),
  recordView: (id: string) => post(`/content/${id}/view`, undefined, undefined, false),

  // 管理端
  adminListCategories: () => get<ContentCategory[]>('/admin/content/categories/tree'),
  adminListCategoriesAll: () => get<ContentCategory[]>('/admin/content/categories'),
  adminCreateCategory: (data: Partial<ContentCategory>) => post<ContentCategory>('/admin/content/categories', data),
  adminUpdateCategory: (id: string, data: Partial<ContentCategory>) => put<ContentCategory>(`/admin/content/categories/${id}`, data),
  adminDisableCategory: (id: string) => post<ContentCategory>(`/admin/content/categories/${id}/disable`),
  adminDeleteCategory: (id: string) => del(`/admin/content/categories/${id}`),

  adminAudiences: () => get<ContentAudience[]>('/admin/content/audiences'),

  adminListContents: (params?: Record<string, unknown>) => get<ContentListResponse>('/admin/content/contents', params),
  adminCreateContent: (data: Partial<ContentDetail>) => post<ContentListItem>('/admin/content/contents', data),
  adminGetContent: (id: string) => get<ContentDetail>(`/admin/content/contents/${id}`),
  adminUpdateContent: (id: string, data: Partial<ContentDetail>) => put<ContentListItem>(`/admin/content/contents/${id}`, data),
  adminPublishContent: (id: string) => post<ContentListItem>(`/admin/content/contents/${id}/publish`),
  adminOfflineContent: (id: string) => post<ContentListItem>(`/admin/content/contents/${id}/offline`),
  adminDeleteContent: (id: string) => del<void>(`/admin/content/contents/${id}`),
  adminBindContentAttachments: (id: string, data: { cover_attachment_id?: string | null; attachment_ids?: string[] }) =>
    put<ContentListItem>(`/admin/content/contents/${id}/attachments`, data),
  adminStats: (params?: { days?: number }) => get<ContentStatsOverview>('/admin/content/statistics/overview', params),

  // ===== 作者端：机构负责人 / 医护人员投稿 =====
  // 与 admin* 接口物理隔离：admin 路由拒收非管理员，作者路由自带归属校验
  authorMe: () => get<AuthorContext>('/author/content/me'),
  authorStats: () => get<AuthorStats>('/author/content/my/stats'),
  authorListContents: (params?: Record<string, unknown>) => get<ContentListResponse>('/author/content/my/contents', params),
  authorCategories: () => get<ContentCategory[]>('/author/content/categories'),
  authorAudiences: () => get<ContentAudience[]>('/author/content/audiences'),
  authorCreateContent: (data: Record<string, unknown>) => post<ContentListItem>('/author/content/contents', data),
  authorGetContent: (id: string) => get<ContentDetail>(`/author/content/contents/${id}`),
  authorUpdateContent: (id: string, data: Record<string, unknown>) => put<ContentListItem>(`/author/content/contents/${id}`, data),
  authorSubmitReview: (id: string) => post<ContentListItem>(`/author/content/contents/${id}/submit`),
  authorWithdrawReview: (id: string) => post<ContentListItem>(`/author/content/contents/${id}/withdraw`),
  authorDeleteContent: (id: string) => del<void>(`/author/content/contents/${id}`),
  authorBindAttachments: (id: string, data: { cover_attachment_id?: string | null; attachment_ids?: string[] }) =>
    put<ContentListItem>(`/author/content/contents/${id}/attachments`, data),
  authorGetTags: (id: string) => get<string[]>(`/author/content/contents/${id}/tags`),
  authorSetTags: (id: string, tagIds: string[]) =>
    put<string[]>(`/author/content/contents/${id}/tags`, { tag_ids: tagIds }),


  // V2.2.1 审核工作流

  adminReviewQueue: (params?: { page?: number; page_size?: number }) => get<ContentListResponse>('/admin/content/review-queue', params),

  adminSubmitReview: (id: string) => post<ContentListItem>('/admin/content/contents/' + id + '/submit-review'),

  adminApproveContent: (id: string, note?: string) => post<ContentListItem>('/admin/content/contents/' + id + '/approve', { note }),

  adminRejectContent: (id: string, note: string) => post<ContentListItem>('/admin/content/contents/' + id + '/reject', { note }),

  // 用户侧互动
  interact: (id: string, data: { action: 'like' | 'favorite' | 'helpful' | 'share'; value?: number; device_type?: string }) =>
    post<{ action: string; active: boolean; count: number; content_id: string }>(`/content/${id}/interact`, data),
  getInteractionStatus: (id: string) =>
    get<{ content_id: string; liked: boolean; favorited: boolean; helpful: boolean; shared: boolean; counts: any }>(`/content/${id}/interaction-status`),
  shareCallback: (id: string) => post<{ count: number; active: boolean }>(`/content/${id}/share-callback`, {}),
  /** 上报阅读进度（15s 心跳 + 离开页面上报），返回当日阅读奖励状态 */
  updateReadProgress: (id: string, data: { read_seconds: number; read_percent: number; device_type?: string }) =>
    post<{
      ok: boolean
      enabled: boolean
      minutes: number
      points: number
      daily_read_seconds: number
      required_seconds: number
      status: 'disabled' | 'locked' | 'claimable' | 'claimed'
    }>(`/content/${id}/read-progress`, data),
  /** 查询当日阅读奖励状态（进入详情页时初始化圆环） */
  getReadRewardStatus: () =>
    get<{
      enabled: boolean
      minutes: number
      points: number
      daily_read_seconds: number
      required_seconds: number
      status: 'disabled' | 'locked' | 'claimable' | 'claimed'
    }>('/content/read-reward/status'),
  /** 手动领取当日阅读奖励（需已达标，每人每天 1 次） */
  claimReadReward: () =>
    post<{ claimed: boolean; already_claimed: boolean; points: number; daily_read_seconds: number; required_seconds: number }>('/content/read-reward/claim', {}),

  // 个性化推荐
  getRecommended: (params?: { limit?: number }) =>
    get<{ items: ContentListItem[]; reason: string; crowd_type?: string; preferred_categories?: string[] }>('/content/_recommended', params),

  // 用户侧收藏 / 阅读历史
  listFavorites: (params?: { page?: number; page_size?: number }) =>
    get<ContentListResponse>('/user/favorites', params),
  removeFavorite: (id: string) => del<void>(`/user/favorites/${id}`),
  listHistory: (params?: { page?: number; page_size?: number }) =>
    get<ContentListResponse & { items: (ContentListItem & { last_viewed_at?: string; read_percent?: number })[] }>('/user/history', params),
  clearHistory: () => del<void>(`/user/history`),

  // 附件
  uploadAttachment: (formData: FormData) =>
    fetch(`${BASE_URL}/upload/content-attachment`, {
      method: 'POST',
      body: formData,
      headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : undefined,
    }).then(async r => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: `HTTP ${r.status}` }))
        throw new Error(err.detail || `上传失败 (${r.status})`)
      }
      return r.json() as Promise<{
        id: string
        attachment_type: string
        file_url: string
        mime_type: string
        file_size: number
        is_primary: number
        status: string
      }>
    }),
  deleteAttachment: (id: string) => del<void>(`/upload/content-attachment/${id}`),

  /**
   * 把正文里已上传的图片「认领」为附件记录（幂等）。
   *
   * 背景：正文图片走 `/api/v1/upload`（只落文件到 static/uploads/，**不建记录**）——
   * 编辑器插图、Word 导入、粘贴图片都是这条路径；而封面必须挂在附件记录上
   * （contents.cover_attachment_id）。所以「提取正文首图 / 封面自动填充」
   * 需要先把正文图补登记成附件。文件已在服务器，不会重新上传。
   *
   * 只接受本站 `/static/uploads/<单层文件名>`，后端会做 realpath 校验。
   */
  claimAttachment: (url: string) =>
    post<ContentAttachment>('/upload/content-attachment/claim', { url }),

  // ============ V2.2.3 batch operations ============
  adminBatchPublish: (ids: string[]) =>
    post<{ total: number; succeeded: number; failed: number; items: { id: string; ok: boolean; error?: string | null }[] }>(
      '/admin/content/contents/batch-publish', { ids }),
  adminBatchOffline: (ids: string[]) =>
    post<{ total: number; succeeded: number; failed: number; items: { id: string; ok: boolean; error?: string | null }[] }>(
      '/admin/content/contents/batch-offline', { ids }),
  adminBatchApprove: (ids: string[]) =>
    post<{ total: number; succeeded: number; failed: number; items: { id: string; ok: boolean; error?: string | null }[] }>(
      '/admin/content/contents/batch-approve', { ids }),
  adminBatchDelete: (ids: string[]) =>
    post<{ total: number; succeeded: number; failed: number; items: { id: string; ok: boolean; error?: string | null }[] }>(
      '/admin/content/contents/batch-delete', { ids }),

  // ============ V2.2.3 content tags binding ============
  adminGetContentTags: (contentId: string) => get<string[]>(`/admin/content/contents/${contentId}/tags`),
  adminSetContentTags: (contentId: string, tagIds: string[]) =>
    put<string[]>(`/admin/content/contents/${contentId}/tags`, { tag_ids: tagIds }),

  // ============ V2.2.3 content tags (admin CRUD) ============
  adminListTags: (params?: { keyword?: string; is_active?: 0 | 1; page?: number; page_size?: number }) =>
    get<{ total: number; items: ContentTag[] }>('/admin/content/tags', params),
  adminCreateTag: (data: { name: string; slug: string }) =>
    post<ContentTag>('/admin/content/tags', data),
  adminUpdateTag: (id: string, data: { name?: string; is_active?: 0 | 1 }) =>
    put<ContentTag>(`/admin/content/tags/${id}`, data),
  adminDeleteTag: (id: string) => del<void>(`/admin/content/tags/${id}`),

  // ============ V2.2.3 user-facing tag listing ============
  listPublicTags: (params?: { keyword?: string; page?: number; page_size?: number }) =>
    get<{ total: number; items: ContentTag[] }>('/content/tags', params),
  listHotTags: (limit?: number) =>
    get<{ items: { id: string; name: string; slug: string; use_count: number }[] }>('/content/tags/hot', { limit }),
  listByTag: (tagId: string, params?: { page?: number; page_size?: number }) =>
    get<{ total: number; items: any[]; tag: ContentTag }>(`/content/tags/${tagId}/contents`, params),

  // ============ V2.2.3 comments ============
  listComments: (contentId: string, params?: { page?: number; page_size?: number }) =>
    get<{ total: number; items: ContentComment[] }>(`/content/${contentId}/comments`, params),
  postComment: (contentId: string, data: { body: string; parent_id?: string }) =>
    post<{ id: string; status: string; created_at: string }>(`/content/${contentId}/comments`, data),
  deleteOwnComment: (commentId: string) => del<void>(`/content/comments/${commentId}`),
  likeComment: (commentId: string) => post<{ liked: boolean; like_count: number }>(`/content/comments/${commentId}/like`, {}),

  // ============ V2.2.3 admin comments management ============
  adminListComments: (params?: { page?: number; page_size?: number; status?: string; keyword?: string; content_title?: string; content_id?: string }) =>
    get<{ total: number; items: AdminContentComment[] }>('/admin/content/comments', params),
  adminApproveComment: (id: string) => post<{ id: string; status: string }>(`/admin/content/comments/${id}/approve`),
  adminRejectComment: (id: string) => post<{ id: string; status: string }>(`/admin/content/comments/${id}/reject`),
  adminHideComment: (id: string) => post<{ id: string; status: string }>(`/admin/content/comments/${id}/hide`),
  adminRestoreComment: (id: string) => post<{ id: string; status: string }>(`/admin/content/comments/${id}/restore`),
  adminDeleteComment: (id: string) => del<void>(`/admin/content/comments/${id}`),
  adminCommentStats: () =>
    get<{ total: number; visible: number; hidden: number; pending: number; today_new: number }>('/admin/content/comments/stats'),

  // ============ V2.2.3 sensitive words admin ============
  adminListSensitiveWords: (params?: { keyword?: string; page?: number; page_size?: number }) =>
    get<SensitiveWordListResponse>('/admin/sensitive-words', params),
  adminCreateSensitiveWord: (data: { word: string; category: SensitiveAction; replacement?: string }) =>
    post<SensitiveWordItem>('/admin/sensitive-words', data),
  adminUpdateSensitiveWord: (id: string, data: { word?: string; category?: SensitiveAction; replacement?: string; is_active?: 0 | 1 }) =>
    put<SensitiveWordItem>(`/admin/sensitive-words/${id}`, data),
  adminDeleteSensitiveWord: (id: string) => del<void>(`/admin/sensitive-words/${id}`),

  // ============ V2.2.3 crowd_type set ============
  setMyCrowdType: (crowd_type: string) => put<unknown>('/users/me', { crowd_type }),
}
